import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Shows the latest active owner announcement as a pop-up when the app opens.
// Per product spec it appears every time the app is opened until the announcement expires.
const AnnouncementPopup = ({ user }) => {
  const [announcement, setAnnouncement] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    axios
      .get(`${API}/announcements/active`)
      .then((res) => {
        if (cancelled) return;
        const list = res.data || [];
        if (list.length > 0) {
          setAnnouncement(list[0]);
          setOpen(true);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [user]);

  if (!open || !announcement) return null;

  const imgSrc = announcement.image_url
    ? (announcement.image_url.startsWith('http') ? announcement.image_url : `${BACKEND_URL}${announcement.image_url}`)
    : null;

  const showText = announcement.show_text !== false && (announcement.title || announcement.body);
  const hasLink = announcement.link_enabled && announcement.link_url;

  const handleLink = () => {
    if (!announcement.link_url) return;
    if (/^https?:\/\//i.test(announcement.link_url)) {
      window.open(announcement.link_url, '_blank', 'noopener');
    } else {
      window.location.href = announcement.link_url;
    }
    setOpen(false);
  };

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      data-testid="announcement-popup"
      onClick={() => setOpen(false)}
    >
      <div
        className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={() => setOpen(false)}
          className="absolute top-3 right-3 z-10 bg-black/40 hover:bg-black/60 text-white rounded-full p-1.5 transition"
          data-testid="announcement-close"
          aria-label="Close announcement"
        >
          <X className="w-5 h-5" />
        </button>

        {imgSrc && (
          <img
            src={imgSrc}
            alt={announcement.title || 'Announcement'}
            className={`w-full object-contain bg-gray-50 ${showText ? 'max-h-[50vh]' : 'max-h-[85vh]'}`}
            data-testid="announcement-image"
          />
        )}

        {showText && (
          <div className="p-6 shrink-0 max-h-[45vh] overflow-y-auto">
            {announcement.title && (
              <h2 className="text-xl font-bold text-gray-900 mb-2" data-testid="announcement-title">
                {announcement.title}
              </h2>
            )}
            {announcement.body && (
              <p className="text-gray-600 text-sm whitespace-pre-wrap leading-relaxed" data-testid="announcement-body">
                {announcement.body}
              </p>
            )}
          </div>
        )}

        {hasLink && (
          <div className={`shrink-0 px-6 pb-6 ${showText ? '' : 'pt-6'}`}>
            <button
              onClick={handleLink}
              className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold py-2.5 rounded-full transition"
              data-testid="announcement-link-btn"
            >
              {announcement.link_label || 'Learn more'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AnnouncementPopup;
